import { useState } from "react"
import { ArrowRight } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { TillFigure } from "@/features/staff/till-figure"
import { TillReportPanel } from "@/features/staff/till-report"
import {
  differenceTone,
  tTill,
  type ShiftCloseSummary,
  type ShiftReport,
} from "@/features/staff/till"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { toneDotClass } from "@/lib/status-colors"

/**
 * How the register was left last time, and the first thing asked at eight in
 * the morning.
 *
 * It used to be the whole Z, pinned to the bottom of the screen whenever no
 * shift was open — the most surface on the page given to the one thing already
 * signed and unchangeable. Here it is four figures and a signature, and the
 * full report is one tap away.
 *
 * **The report is offered only when the page is actually holding one.** The
 * summary is always the last CLOSED shift; the `report` prop is the shift the
 * screen is about, which is a different one while a drawer is open. When the
 * two ids match, the full Z is in memory and the drawer opens it. When they do
 * not, there is no link — a control whose only outcome is a screen we cannot
 * draw is worse than no control (the honest-UI rule).
 *
 * The difference carries a dot and never a coloured figure: `-$40.00` in red
 * fails contrast at the size a number this important is set, and the label
 * beside it already says whether it was short or over.
 */

const DIFFERENCE_TONE = {
  balanced: "success",
  over: "warning",
  short: "warning",
} as const

type TillPreviousCloseProps = {
  summary: ShiftCloseSummary
  timeZone: string
  /** The full Z for this same shift, when the page happens to hold it. */
  report: ShiftReport | null
}

export function TillPreviousClose({ summary, timeZone, report }: TillPreviousCloseProps) {
  const [reportOpen, setReportOpen] = useState(false)

  const difference = summary.difference === null ? null : Number(summary.difference)
  const tone = difference === null ? null : differenceTone(difference)

  return (
    <>
      <Card density="compact" data-testid="shift-previous-close">
        <CardHeader className="border-b">
          <CardTitle>{tTill("previous_close_title")}</CardTitle>
          <CardDescription>
            {tTill("previous_close_signed", {
              name: summary.closedBy ?? tTill("unknown_actor"),
              time: formatDateTime(summary.closedAt, { timeZone }),
            })}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <TillFigure
              label={tTill("expected_cash")}
              value={summary.expectedCash === null ? "—" : formatMoney(summary.expectedCash)}
            />
            <TillFigure
              label={tTill("declared_label")}
              value={summary.declaredCash === null ? "—" : formatMoney(summary.declaredCash)}
            />
          </div>

          <div className="space-y-1 border-t pt-4">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {tone ? <span aria-hidden="true" className={toneDotClass(DIFFERENCE_TONE[tone])} /> : null}
              {tTill("difference_label")}
              {tone ? ` · ${tTill(`difference_${tone}`)}` : null}
            </p>
            <p
              className="text-lg font-semibold tabular-nums text-foreground"
              data-testid="shift-previous-difference"
            >
              {summary.difference === null ? "—" : formatMoney(summary.difference)}
            </p>
          </div>
        </CardContent>

        {report ? (
          <CardFooter>
            <Button
              type="button"
              variant="link"
              className="px-0"
              onClick={() => setReportOpen(true)}
              data-testid="shift-previous-report-open"
            >
              {tTill("previous_close_open_report")}
              <ArrowRight className="size-4" />
            </Button>
          </CardFooter>
        ) : null}
      </Card>

      {report ? (
        <ResponsiveDrawer open={reportOpen} onOpenChange={setReportOpen}>
          <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-xl">
            <DrawerHeader>
              <DrawerTitle>{tTill("previous_close_report_title")}</DrawerTitle>
            </DrawerHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
              <TillReportPanel report={report} timeZone={timeZone} />
            </div>
          </DrawerContent>
        </ResponsiveDrawer>
      ) : null}
    </>
  )
}
